import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Pencil, Plus, Search, Star, Tags } from "lucide-react";
import { toast } from "sonner";
import type { AxiosError } from "axios";

import { Button } from "@/marketing/components/ui/button";
import { cn } from "@/marketing/lib/utils";
import { formatDate, safeHostname } from "@/marketing/lib/formatters";
import type {
  CommunityPostStatus,
  CommunityPostView,
  CreateCommentRequest,
} from "@/marketing/models/community";
import { communityAdminAPIClient } from "@/marketing/infrastructure/influencer/community/communityAdminAPIClient";
import { COMMUNITY_KEYS } from "@/marketing/infrastructure/influencer/community/utils/keys";
import { COMMUNITY_QUERIES } from "@/marketing/infrastructure/influencer/community/utils/queries";

import { PickFormDialog } from "./PickFormDialog";
import { GuideFormDialog } from "./GuideFormDialog";
import { CommunityDetailPanel } from "./CommunityDetailPanel";
import { SeedPostDialog } from "./SeedPostDialog";
import { TrendingManualDialog } from "./TrendingManualDialog";
import { CategoryManagerDialog } from "./CategoryManagerDialog";

type CommunityTab = "picks" | "guide" | "help" | "trend";

const TABS: { id: CommunityTab; label: string }[] = [
  { id: "picks", label: "★ Skin Seoul Picks" },
  { id: "guide", label: "Creator Guide" },
  { id: "help", label: "Help Lounge" },
  { id: "trend", label: "Trending" },
];

export function CommunityPage() {
  const [activeTab, setActiveTab] = useState<CommunityTab>("picks");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CommunityPostView | undefined>(undefined);
  const [guideDialogOpen, setGuideDialogOpen] = useState(false);
  const [editingGuide, setEditingGuide] = useState<CommunityPostView | undefined>(undefined);

  const openCreate = () => {
    setEditing(undefined);
    setDialogOpen(true);
  };
  const openEdit = (pick: CommunityPostView) => {
    setEditing(pick);
    setDialogOpen(true);
  };
  const openGuideCreate = () => {
    setEditingGuide(undefined);
    setGuideDialogOpen(true);
  };
  const openGuideEdit = (guide: CommunityPostView) => {
    setEditingGuide(guide);
    setGuideDialogOpen(true);
  };

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-gap-4 tw-p-6">
      <div>
        <h1 className="tw-text-2xl tw-font-semibold tw-text-gray-900">Community</h1>
        <p className="tw-mt-1 tw-text-sm tw-text-gray-500">
          Curate picks, moderate posts, and watch what&apos;s trending.
        </p>
      </div>

      <div className="tw-flex tw-gap-1 tw-border-b tw-border-gray-200">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "tw-relative tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-gray-500 tw-transition-colors",
              activeTab === tab.id && "tw-font-semibold tw-text-gray-900",
            )}
          >
            {tab.label}
            {activeTab === tab.id && (
              <span className="tw-absolute tw-inset-x-2 -tw-bottom-px tw-h-0.5 tw-rounded tw-bg-violet-600" />
            )}
          </button>
        ))}
      </div>

      {activeTab === "picks" && <PicksPane onCreate={openCreate} onEdit={openEdit} />}
      {activeTab === "guide" && <GuidesPane onCreate={openGuideCreate} onEdit={openGuideEdit} />}
      {activeTab === "help" && <HelpPane />}
      {activeTab === "trend" && <TrendingPane />}

      <PickFormDialog open={dialogOpen} onOpenChange={setDialogOpen} pick={editing} />
      <GuideFormDialog open={guideDialogOpen} onOpenChange={setGuideDialogOpen} guide={editingGuide} />
    </div>
  );
}

// Strips HTML tags for a plain-text list preview of a guide article body.
function htmlToPreview(html: string): string {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function GuidesPane({
  onCreate,
  onEdit,
}: {
  onCreate: () => void;
  onEdit: (guide: CommunityPostView) => void;
}) {
  const queryClient = useQueryClient();
  const { data: guides = [], isLoading, isError } = useQuery(COMMUNITY_QUERIES.guides());

  const reorder = useMutation({
    mutationFn: (orderedPostIds: string[]) => communityAdminAPIClient.reorderGuides(orderedPostIds),
    onSuccess: (updated) => {
      queryClient.setQueryData(COMMUNITY_KEYS.guides(), updated);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ?? "Failed to reorder");
      queryClient.invalidateQueries({ queryKey: COMMUNITY_KEYS.guides() });
    },
  });

  const hide = useMutation({
    mutationFn: (postId: string) => communityAdminAPIClient.updateGuide(postId, { status: "HIDDEN" }),
    onSuccess: () => {
      toast.success("Guide hidden");
      queryClient.invalidateQueries({ queryKey: COMMUNITY_KEYS.guides() });
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ?? "Failed to hide");
    },
  });

  const orderedIds = useMemo(() => guides.map((guide) => guide.postId), [guides]);

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= orderedIds.length) return;
    const next = [...orderedIds];
    const current = next[index];
    const swapped = next[target];
    if (current === undefined || swapped === undefined) return;
    next[index] = swapped;
    next[target] = current;
    reorder.mutate(next);
  };

  if (isLoading) {
    return <div className="tw-py-10 tw-text-center tw-text-sm tw-text-gray-400">Loading guides...</div>;
  }
  if (isError) {
    return <div className="tw-py-10 tw-text-center tw-text-sm tw-text-red-500">Failed to load guides.</div>;
  }

  return (
    <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-3 tw-overflow-y-auto">
      <div className="tw-flex tw-items-center tw-justify-between">
        <p className="tw-text-sm tw-text-gray-500">
          Order articles with the arrows — the top guide appears first in the creator app.
        </p>
        <Button onClick={onCreate}>
          <Plus className="tw-mr-1 tw-h-4 tw-w-4" /> New Guide
        </Button>
      </div>

      {guides.length === 0 && (
        <button
          type="button"
          onClick={onCreate}
          className="tw-rounded-xl tw-border tw-border-dashed tw-border-gray-200 tw-py-8 tw-text-sm tw-text-gray-400 hover:tw-border-violet-400 hover:tw-text-violet-700"
        >
          + Write the first Creator Guide article
        </button>
      )}

      {guides.map((guide, index) => (
        <article
          key={guide.postId}
          className="tw-flex tw-overflow-hidden tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white hover:tw-border-gray-300"
        >
          <div className="tw-flex tw-w-12 tw-flex-none tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-border-r tw-border-gray-200 tw-bg-gray-50 tw-py-3">
            <button
              type="button"
              aria-label="Move guide up"
              className="tw-text-gray-400 hover:tw-text-gray-900 disabled:tw-opacity-30"
              onClick={() => move(index, -1)}
              disabled={index === 0 || reorder.isPending}
            >
              <ChevronUp className="tw-h-4 tw-w-4" />
            </button>
            <span className="tw-text-base tw-font-bold tw-tabular-nums">{index + 1}</span>
            <button
              type="button"
              aria-label="Move guide down"
              className="tw-text-gray-400 hover:tw-text-gray-900 disabled:tw-opacity-30"
              onClick={() => move(index, 1)}
              disabled={index === guides.length - 1 || reorder.isPending}
            >
              <ChevronDown className="tw-h-4 tw-w-4" />
            </button>
          </div>

          <div className="tw-flex-1 tw-p-4">
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
              <StatusBadge status={guide.status} />
              <div className="tw-flex tw-flex-none tw-gap-2">
                <Button variant="outline" size="sm" onClick={() => onEdit(guide)}>
                  <Pencil className="tw-mr-1 tw-h-3.5 tw-w-3.5" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="tw-text-red-600"
                  onClick={() => hide.mutate(guide.postId)}
                  disabled={hide.isPending}
                >
                  Hide
                </Button>
              </div>
            </div>

            <h3 className="tw-mt-2.5 tw-font-semibold tw-text-gray-900">{guide.title || "(untitled guide)"}</h3>
            <p className="tw-mt-1 tw-line-clamp-2 tw-text-sm tw-text-gray-500">{htmlToPreview(guide.body)}</p>
          </div>
        </article>
      ))}
    </div>
  );
}

function TrendingPane() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery(COMMUNITY_QUERIES.trending());
  const [addOpen, setAddOpen] = useState(false);
  const items = data?.items ?? [];
  const computedAt = items.find((i) => !i.isManual)?.computedAt;

  const unpin = useMutation({
    mutationFn: (contentKey: string) => communityAdminAPIClient.deleteManualTrending(contentKey),
    onSuccess: () => {
      toast.success("Unpinned");
      queryClient.invalidateQueries({ queryKey: COMMUNITY_KEYS.trending() });
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ?? "Failed to unpin");
    },
  });

  if (isLoading) {
    return <div className="tw-py-10 tw-text-center tw-text-sm tw-text-gray-400">Loading trending...</div>;
  }
  if (isError) {
    return <div className="tw-py-10 tw-text-center tw-text-sm tw-text-red-500">Failed to load trending.</div>;
  }

  return (
    <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-3 tw-overflow-y-auto">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
        <p className="tw-text-sm tw-text-gray-500">
          Ranked by momentum (Δviews + 3×Δlikes, 7-day window). Pinned items stay on top.
        </p>
        <div className="tw-flex tw-items-center tw-gap-3">
          {computedAt && (
            <span className="tw-text-xs tw-text-gray-400">Last computed {formatDate(computedAt)}</span>
          )}
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="tw-mr-1 tw-h-4 tw-w-4" /> Add video
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="tw-grid tw-flex-1 tw-place-items-center tw-rounded-xl tw-border tw-border-dashed tw-border-gray-200 tw-py-12 tw-text-sm tw-text-gray-400">
          No ranking yet — add a video or wait for the momentum batch.
        </div>
      ) : (
        <div className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-gray-200">
          <table className="tw-w-full tw-text-sm">
            <thead className="tw-bg-gray-50 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-gray-400">
              <tr>
                <th className="tw-w-20 tw-px-4 tw-py-2.5">Rank</th>
                <th className="tw-px-4 tw-py-2.5">Content</th>
                <th className="tw-px-4 tw-py-2.5">Creator</th>
                <th className="tw-px-4 tw-py-2.5">Campaign</th>
                <th className="tw-px-4 tw-py-2.5 tw-text-right">Momentum (7d)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.contentKey}
                  className={cn(
                    "tw-border-t tw-border-gray-100 hover:tw-bg-gray-50/60",
                    item.isManual && "tw-bg-amber-50/40",
                  )}
                >
                  <td className="tw-px-4 tw-py-3">
                    {item.isManual ? (
                      <span title="Pinned by admin" className="tw-text-base">📌</span>
                    ) : (
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <span className="tw-text-base tw-font-bold tw-tabular-nums">{item.rank}</span>
                        <RankDelta change={item.rankChange} />
                      </div>
                    )}
                  </td>
                  <td className="tw-max-w-[320px] tw-px-4 tw-py-3">
                    <a
                      href={item.contentUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="tw-font-medium tw-text-gray-900 hover:tw-text-violet-600"
                    >
                      {item.platform} · {safeHostname(item.contentUrl)}
                    </a>
                    <div className="tw-text-xs tw-text-gray-400">
                      ♥ {item.likeCount.toLocaleString()} · ▶ {item.viewCount.toLocaleString()}
                    </div>
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-text-gray-600">
                    {item.author?.displayName ??
                      (item.influencerUserId ? `user ${item.influencerUserId}` : "—")}
                  </td>
                  <td className="tw-px-4 tw-py-3">
                    {item.campaign ? (
                      <span className="tw-rounded-full tw-bg-violet-100 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-violet-700">
                        {item.campaign.name}
                      </span>
                    ) : (
                      <span className="tw-text-gray-400">—</span>
                    )}
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-text-right">
                    {item.isManual ? (
                      <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                        <span className="tw-rounded-full tw-bg-amber-100 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-amber-700">
                          Pinned
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="tw-text-red-600"
                          onClick={() => unpin.mutate(item.contentKey)}
                          disabled={unpin.isPending}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <span className="tw-font-semibold tw-text-emerald-600">
                        {item.score.toLocaleString()}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TrendingManualDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}

function RankDelta({ change }: { change?: number | undefined }) {
  if (change === undefined || change === 0) {
    return <span className="tw-text-xs tw-font-bold tw-text-gray-400">—</span>;
  }
  if (change > 0) {
    return <span className="tw-text-xs tw-font-bold tw-text-emerald-500">▲{change}</span>;
  }
  return <span className="tw-text-xs tw-font-bold tw-text-red-500">▼{Math.abs(change)}</span>;
}

const STATUS_FILTERS: { label: string; value?: CommunityPostStatus | undefined }[] = [
  { label: "All", value: undefined },
  { label: "Published", value: "PUBLISHED" },
  { label: "Hidden", value: "HIDDEN" },
  { label: "Removed", value: "REMOVED" },
];

const HELP_SORTS: { label: string; value: "newest" | "likes" }[] = [
  { label: "Sort: Newest", value: "newest" },
  { label: "Sort: Most liked", value: "likes" },
];

function HelpPane() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<CommunityPostStatus | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<"newest" | "likes">("newest");
  const [selected, setSelected] = useState<CommunityPostView | null>(null);
  const [seedOpen, setSeedOpen] = useState(false);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);

  const { data: posts = [], isLoading, isError } = useQuery(COMMUNITY_QUERIES.posts("HELP", status));

  const selectedId = selected?.postId;
  const commentsQuery = useQuery({
    ...COMMUNITY_QUERIES.comments(selectedId ?? "none"),
    enabled: !!selectedId,
  });

  const moderate = useMutation({
    mutationFn: ({ postId, next }: { postId: string; next: CommunityPostStatus }) =>
      communityAdminAPIClient.moderatePost(postId, next),
    onSuccess: (_data, { next }) => {
      toast.success(next === "PUBLISHED" ? "Post restored" : next === "REMOVED" ? "Post deleted" : "Post hidden");
      queryClient.invalidateQueries({ queryKey: COMMUNITY_KEYS.all });
      setSelected(null);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ?? "Failed to moderate");
    },
  });

  const postComment = useMutation({
    mutationFn: ({ postId, request }: { postId: string; request: CreateCommentRequest }) =>
      communityAdminAPIClient.createComment(postId, request),
    onSuccess: (_data, { postId }) => {
      toast.success("Comment posted");
      queryClient.invalidateQueries({ queryKey: COMMUNITY_KEYS.comments(postId) });
      queryClient.invalidateQueries({ queryKey: COMMUNITY_KEYS.posts("HELP", status) });
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ?? "Failed to post comment");
    },
  });

  const { data: allCategories = [] } = useQuery(COMMUNITY_QUERIES.categories());
  const categories = useMemo(
    () => allCategories.filter((category) => category.isActive),
    [allCategories],
  );
  // Fall back to "All" if the selected category was deactivated/removed.
  const activeCategory = categories.some((c) => c.categoryId === category) ? category : undefined;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const result = posts.filter((post) => {
      const matchesSearch =
        q === "" ||
        (post.title ?? "").toLowerCase().includes(q) ||
        post.body.toLowerCase().includes(q);
      const matchesCategory =
        !activeCategory || post.categories.some((c) => c.categoryId === activeCategory);
      return matchesSearch && matchesCategory;
    });
    return result.sort((a, b) =>
      sort === "likes" ? b.likeCount - a.likeCount : b.createdAt.localeCompare(a.createdAt),
    );
  }, [posts, search, activeCategory, sort]);

  return (
    <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-3 tw-overflow-y-auto">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
        <p className="tw-text-sm tw-text-gray-500">
          Creator Q&amp;A. Seed a post as a creator to kick-start the board.
        </p>
        <div className="tw-flex tw-items-center tw-gap-2">
          <Button variant="outline" onClick={() => setCategoryManagerOpen(true)}>
            <Tags className="tw-mr-1 tw-h-4 tw-w-4" /> Manage Categories
          </Button>
          <Button onClick={() => setSeedOpen(true)}>
            <Plus className="tw-mr-1 tw-h-4 tw-w-4" /> Create post
          </Button>
        </div>
      </div>

      <div className="tw-flex tw-items-center tw-gap-2">
        <div className="tw-relative tw-shrink-0">
          <Search className="tw-pointer-events-none tw-absolute tw-left-2.5 tw-top-1/2 tw-h-3.5 tw-w-3.5 -tw-translate-y-1/2 tw-text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search posts..."
            className="tw-w-56 tw-rounded-lg tw-border tw-border-gray-200 tw-py-1.5 tw-pl-8 tw-pr-3 tw-text-sm tw-outline-none focus:tw-border-violet-500"
          />
        </div>
        {/* Category pills scroll right past the edge instead of wrapping onto new rows. */}
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2 tw-overflow-x-auto tw-pb-1">
          <button
            type="button"
            onClick={() => setCategory(undefined)}
            className={cn(
              "tw-shrink-0 tw-rounded-full tw-border tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium",
              !activeCategory
                ? "tw-border-gray-900 tw-bg-gray-900 tw-text-white"
                : "tw-border-gray-200 tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50",
            )}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c.categoryId}
              type="button"
              onClick={() => setCategory(c.categoryId)}
              className={cn(
                "tw-shrink-0 tw-rounded-full tw-border tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium",
                activeCategory === c.categoryId
                  ? "tw-border-gray-900 tw-bg-gray-900 tw-text-white"
                  : "tw-border-gray-200 tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50",
              )}
            >
              {c.name}
            </button>
          ))}
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as "newest" | "likes")}
          className="tw-shrink-0 tw-rounded-lg tw-border tw-border-gray-200 tw-px-2.5 tw-py-1.5 tw-text-xs tw-text-gray-600 tw-outline-none"
        >
          {HELP_SORTS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          value={status ?? ""}
          onChange={(e) => setStatus((e.target.value || undefined) as CommunityPostStatus | undefined)}
          className="tw-shrink-0 tw-rounded-lg tw-border tw-border-gray-200 tw-px-2.5 tw-py-1.5 tw-text-xs tw-text-gray-600 tw-outline-none"
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.label} value={f.value ?? ""}>
              {f.label}
            </option>
          ))}
        </select>
      </div>

      {isLoading && <div className="tw-py-10 tw-text-center tw-text-sm tw-text-gray-400">Loading posts...</div>}
      {isError && <div className="tw-py-10 tw-text-center tw-text-sm tw-text-red-500">Failed to load posts.</div>}

      {!isLoading && !isError && visible.length === 0 && (
        <div className="tw-rounded-xl tw-border tw-border-dashed tw-border-gray-200 tw-py-12 tw-text-center tw-text-sm tw-text-gray-400">
          No posts.
        </div>
      )}

      {!isLoading && !isError && visible.length > 0 && (
        <div className="tw-flex tw-flex-col tw-gap-3">
          {visible.map((post) => (
            <article
              key={post.postId}
              onClick={() => setSelected(post)}
              className="tw-cursor-pointer tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-p-4 hover:tw-border-gray-300"
            >
              <div className="tw-flex tw-items-center tw-gap-2.5">
                {post.author.avatarUrl ? (
                  <img src={post.author.avatarUrl} alt="" className="tw-h-8 tw-w-8 tw-flex-none tw-rounded-full tw-object-cover" />
                ) : (
                  <div className="tw-grid tw-h-8 tw-w-8 tw-flex-none tw-place-items-center tw-rounded-full tw-bg-gray-900 tw-text-xs tw-font-semibold tw-text-white">
                    {(post.author.displayName ?? "U").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="tw-min-w-0 tw-flex-1">
                  <div className="tw-truncate tw-text-sm tw-font-medium tw-text-gray-900">
                    {post.author.displayName ?? `user ${post.author.userId}`}
                  </div>
                  <div className="tw-text-xs tw-text-gray-400">{post.createdAt.slice(0, 10)}</div>
                </div>
                <StatusBadge status={post.status} />
              </div>

              {post.title && <h3 className="tw-mt-3 tw-font-semibold tw-text-gray-900">{post.title}</h3>}
              <p className="tw-mt-1 tw-line-clamp-3 tw-text-sm tw-text-gray-600">{post.body}</p>

              {post.imageUrls.length > 0 && (
                <div className="tw-mt-3 tw-flex tw-gap-2">
                  {post.imageUrls.slice(0, 4).map((url) => (
                    <img key={url} src={url} alt="" className="tw-h-20 tw-w-20 tw-flex-none tw-rounded-lg tw-object-cover" />
                  ))}
                </div>
              )}

              <div className="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-gray-400">
                {post.campaign && (
                  <span className="tw-rounded-full tw-bg-violet-100 tw-px-2 tw-py-0.5 tw-font-medium tw-text-violet-700">
                    ◎ {post.campaign.name}
                  </span>
                )}
                {post.categories.map((c) => (
                  <span key={c.categoryId} className="tw-rounded-full tw-bg-gray-100 tw-px-2 tw-py-0.5 tw-text-gray-600">
                    {c.name}
                  </span>
                ))}
                <span className="tw-ml-auto">♥ {post.likeCount} · 💬 {post.commentCount}</span>
              </div>
            </article>
          ))}
        </div>
      )}

      <CommunityDetailPanel
        post={selected}
        onClose={() => setSelected(null)}
        comments={commentsQuery.data?.comments}
        commentsLoading={commentsQuery.isLoading}
        onModerate={(post, next) => moderate.mutate({ postId: post.postId, next })}
        onPostComment={
          selectedId
            ? (request) => postComment.mutate({ postId: selectedId, request })
            : undefined
        }
        postingComment={postComment.isPending}
      />
      <SeedPostDialog open={seedOpen} onOpenChange={setSeedOpen} />
      <CategoryManagerDialog open={categoryManagerOpen} onOpenChange={setCategoryManagerOpen} />
    </div>
  );
}

function PicksPane({
  onCreate,
  onEdit,
}: {
  onCreate: () => void;
  onEdit: (pick: CommunityPostView) => void;
}) {
  const queryClient = useQueryClient();
  const { data: picks = [], isLoading, isError } = useQuery(COMMUNITY_QUERIES.picks());
  const [selected, setSelected] = useState<CommunityPostView | null>(null);

  const reorder = useMutation({
    mutationFn: (orderedPostIds: string[]) =>
      communityAdminAPIClient.reorderPicks(orderedPostIds),
    onSuccess: (updated) => {
      queryClient.setQueryData(COMMUNITY_KEYS.picks(), updated);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ?? "Failed to reorder");
      queryClient.invalidateQueries({ queryKey: COMMUNITY_KEYS.picks() });
    },
  });

  const hide = useMutation({
    mutationFn: (postId: string) =>
      communityAdminAPIClient.updatePick(postId, { status: "HIDDEN" }),
    onSuccess: () => {
      toast.success("Pick hidden");
      queryClient.invalidateQueries({ queryKey: COMMUNITY_KEYS.picks() });
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ?? "Failed to hide");
    },
  });

  const orderedIds = useMemo(() => picks.map((pick) => pick.postId), [picks]);

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= orderedIds.length) return;
    const next = [...orderedIds];
    const current = next[index];
    const swapped = next[target];
    if (current === undefined || swapped === undefined) return;
    next[index] = swapped;
    next[target] = current;
    reorder.mutate(next);
  };

  if (isLoading) {
    return <div className="tw-py-10 tw-text-center tw-text-sm tw-text-gray-400">Loading picks...</div>;
  }
  if (isError) {
    return <div className="tw-py-10 tw-text-center tw-text-sm tw-text-red-500">Failed to load picks.</div>;
  }

  return (
    <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-3 tw-overflow-y-auto">
      <div className="tw-flex tw-items-center tw-justify-between">
        <p className="tw-text-sm tw-text-gray-500">
          Drag order with the arrows — the top pick appears first in the creator app.
        </p>
        <Button onClick={onCreate}>
          <Plus className="tw-mr-1 tw-h-4 tw-w-4" /> New Pick
        </Button>
      </div>

      {picks.length === 0 && (
        <button
          type="button"
          onClick={onCreate}
          className="tw-rounded-xl tw-border tw-border-dashed tw-border-gray-200 tw-py-8 tw-text-sm tw-text-gray-400 hover:tw-border-violet-400 hover:tw-text-violet-700"
        >
          + Create the first Skin Seoul Pick
        </button>
      )}

      {picks.map((pick, index) => (
        <article
          key={pick.postId}
          onClick={() => setSelected(pick)}
          className="tw-flex tw-cursor-pointer tw-overflow-hidden tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white hover:tw-border-gray-300"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="tw-flex tw-w-12 tw-flex-none tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-border-r tw-border-gray-200 tw-bg-gray-50 tw-py-3"
          >
            <button
              type="button"
              className="tw-text-gray-400 hover:tw-text-gray-900 disabled:tw-opacity-30"
              onClick={() => move(index, -1)}
              disabled={index === 0 || reorder.isPending}
            >
              <ChevronUp className="tw-h-4 tw-w-4" />
            </button>
            <span className="tw-text-base tw-font-bold tw-tabular-nums">{index + 1}</span>
            <button
              type="button"
              className="tw-text-gray-400 hover:tw-text-gray-900 disabled:tw-opacity-30"
              onClick={() => move(index, 1)}
              disabled={index === picks.length - 1 || reorder.isPending}
            >
              <ChevronDown className="tw-h-4 tw-w-4" />
            </button>
          </div>

          <div className="tw-flex-1 tw-p-4">
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <StatusBadge status={pick.status} />
                {pick.campaign && (
                  <span className="tw-rounded-full tw-bg-violet-100 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-violet-700">
                    ◎ {pick.campaign.name}
                    {pick.campaign.brandName ? ` · ${pick.campaign.brandName}` : ""}
                  </span>
                )}
              </div>
              <div className="tw-flex tw-flex-none tw-gap-2" onClick={(e) => e.stopPropagation()}>
                <Button variant="outline" size="sm" onClick={() => onEdit(pick)}>
                  <Pencil className="tw-mr-1 tw-h-3.5 tw-w-3.5" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="tw-text-red-600"
                  onClick={() => hide.mutate(pick.postId)}
                  disabled={hide.isPending}
                >
                  Hide
                </Button>
              </div>
            </div>

            <h3 className="tw-mt-2.5 tw-font-semibold tw-text-gray-900">
              {pick.title || "(untitled pick)"}
            </h3>
            <p className="tw-mt-1 tw-line-clamp-2 tw-text-sm tw-text-gray-500">{pick.body}</p>

            <div className="tw-mt-3 tw-flex tw-items-center tw-gap-3 tw-text-xs tw-text-gray-400">
              <span className="tw-flex tw-items-center tw-gap-1">
                <Star className="tw-h-3.5 tw-w-3.5" /> {pick.featuredContents.length} featured
              </span>
              <span>♥ {pick.likeCount}</span>
              <span>by {pick.author.displayName ?? "Skin Seoul Team"}</span>
            </div>
          </div>
        </article>
      ))}

      <CommunityDetailPanel
        post={selected}
        onClose={() => setSelected(null)}
        onEdit={(pick) => {
          setSelected(null);
          onEdit(pick);
        }}
        onHide={(pick) => {
          hide.mutate(pick.postId);
          setSelected(null);
        }}
      />
    </div>
  );
}

function StatusBadge({ status }: { status: CommunityPostView["status"] }) {
  const map: Record<CommunityPostView["status"], string> = {
    PUBLISHED: "tw-bg-green-100 tw-text-green-700",
    HIDDEN: "tw-bg-gray-100 tw-text-gray-500",
    REMOVED: "tw-bg-red-100 tw-text-red-700",
  };
  return (
    <span className={cn("tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium", map[status])}>
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}
